// ===== Updated PopularCategoriesInLocation.tsx =====
import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader } from 'reactstrap';
import { Link } from 'react-router-dom';
import { Contractor } from '../../models/Contractor.interface';
import { buildCategoryLocationUrl, buildCategoryUrl } from '../../utils/urlUtils';
import PopularCategoriesInLocationSkeleton from './PopularCategoriesInLocationSkeleton';

interface PopularCategoriesInLocationProps {
  contractors: Contractor[] | undefined;
  state: string;
  city?: string;
  title?: string;
  loading?: boolean;
}

interface CategoryData {
  name: string;
  count: number;
}

const PopularCategoriesInLocation: React.FC<PopularCategoriesInLocationProps> = ({ 
  contractors, 
  state,
  city,
  title,
  loading = false
}) => {
  const [categories, setCategories] = useState<CategoryData[]>([]);

  useEffect(() => {
    // Guard against undefined contractors
    if (!contractors || !Array.isArray(contractors)) {
      setCategories([]);
      return;
    }

    // Extract all categories from contractors
    const categoryMap = new Map<string, number>();
    
    contractors.forEach(contractor => {
      if (contractor.categories && Array.isArray(contractor.categories)) {
        contractor.categories.forEach(category => {
          const count = categoryMap.get(category) || 0;
          categoryMap.set(category, count + 1);
        });
      }
    });

    // Convert to array, sort by count, take top 10
    const categoriesArray = Array.from(categoryMap.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); // Limit to top 10
    
    setCategories(categoriesArray);
  }, [contractors]);

  // Show skeleton while loading
  if (loading) {
    return (
      <PopularCategoriesInLocationSkeleton 
        title={title || (city ? `Popular Services in ${city}` : 'Popular Categories')}
      />
    );
  }

  // Show skeleton if contractors is undefined (still loading)
  if (!contractors) {
    return (
      <PopularCategoriesInLocationSkeleton 
        title={title || (city ? `Popular Services in ${city}` : 'Popular Categories')}
      />
    );
  }

  if (categories.length === 0) {
    return (
      <Card className="mb-4">
        <CardHeader>
          <h5 className="card-title mb-0">
            {title || (city ? `Popular Services in ${city}` : 'Popular Categories')}
          </h5>
        </CardHeader>
        <CardBody>
          <p className="text-muted">No category data available.</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="mb-4">
      <CardHeader>
        <h5 className="card-title mb-0">
          {title || (city ? `Popular Services in ${city}` : 'Popular Categories')}
        </h5>
      </CardHeader>
      <CardBody>
        <div className="list-group list-group-flush">
          {categories.map((category, index) => (
            <Link 
              key={index}
              to={city 
                ? buildCategoryLocationUrl(state, city, category.name)
                : buildCategoryUrl(category.name)
              }
              className="list-group-item list-group-item-action d-flex justify-content-between align-items-center border-0 py-3"
            >
              <span className="text-dark">{category.name}</span>
              <span className="badge bg-secondary rounded-pill">{category.count}</span>
            </Link>
          ))}
        </div>
      </CardBody>
    </Card>
  );
};

export default PopularCategoriesInLocation;